import { z } from 'zod'

/**
 * A Grant is a scoped, revocable token for sharing: a capture-only token for
 * someone's Shortcut, or a link to one list/page/project. Only the sha256 of
 * the secret is stored; the secret is shown once when the grant is made.
 */
export const SCOPE_RE =
  /^(capture|calendar|prime|lead|mcp|library:read|schedule:read|(task|project|page|list|date|person):(read|write)|ws:[a-z0-9][a-z0-9-]{0,39}|workspace:[a-z0-9][a-z0-9-]{0,30}:(read|write)|(list|page|task|project):[0-9a-f-]{36}:(read|write)|file:[0-9a-f-]{36}:(read|drop)|files-sync:[0-9a-f-]{36})$/

export const grantSpec = z
  .object({
    name: z.string().min(1).max(120),
    scopes: z.array(z.string().regex(SCOPE_RE)).min(1).max(60),
    /** hex(sha256(secret)) */
    token_hash: z.string().regex(/^[0-9a-f]{64}$/),
    /** RFC 3339 UTC; absent = permanent */
    expires_at: z.string().optional(),
    /** 0 = unlimited, 1 = one-time */
    max_uses: z.number().int().min(0).max(100_000).optional(),
    /** from: label stamped on captures made with this grant */
    from: z.string().max(64).optional(),
    /** turns the share page into a capture form with this prompt; shown to the visitor */
    purpose: z.string().max(300).optional(),
    /** private guidance for the assistant that rides along with each capture; never shown */
    context: z.string().max(2000).optional(),
    /** the capture page: a line under the heading, one-tap starters, extra fields, a daily cap, a pause */
    intro: z.string().max(300).optional(),
    buttons: z.array(z.string().min(1).max(40)).max(6).optional(),
    fields: z
      .array(z.enum(['details', 'when', 'link']))
      .max(3)
      .optional(),
    daily_limit: z.number().int().min(0).max(1000).optional(),
    paused: z.boolean().optional(),
    /** the owner's latest note to this link's holder, shown on the capture page; when it was written */
    reply: z.string().max(280).optional(),
    reply_at: z.string().optional(),
    /** the preview card a messaging app shows: its own words (else the page's) and one emoji */
    card_title: z.string().max(120).optional(),
    card_text: z.string().max(200).optional(),
    card_icon: z.string().max(16).optional(),
    /** an API token for /api/v1 rather than a share link; its writes wait (suggest) or apply (direct) */
    api: z.boolean().optional(),
    writes: z.enum(['suggest', 'direct']).optional(),
    /** a workspace member the owner made admin (2026-10-17): the whole app but what is his alone */
    admin: z.boolean().optional(),
    /** what a member is called in the app, in place of the grant's name */
    member_name: z.string().max(60).optional(),
    /** file shares (2026-10-31, docs/areas/files.md): an argon2id hash the server made */
    password_hash: z
      .string()
      .regex(/^\$argon2id\$v=19\$m=[0-9]+,t=[0-9]+,p=[0-9]+\$[A-Za-z0-9+/]+\$[A-Za-z0-9+/]+$/)
      .max(200)
      .optional(),
    /** ends the link after this many downloads */
    max_downloads: z.number().int().min(0).max(100_000).optional(),
    /** hides Download on the share page (it cannot stop a determined visitor) */
    view_only: z.boolean().optional(),
    /** a file request's largest file, and how many files in all */
    drop_max_bytes: z.number().int().min(0).max(5_368_709_120).optional(),
    drop_max_files: z.number().int().min(0).max(1000).optional(),
  })
  .strict()
export type GrantSpec = z.infer<typeof grantSpec>

export const grantStatus = z.object({
  use_count: z.number().int().default(0),
  last_used_at: z.string().optional(),
  revoked_at: z.string().optional(),
  /** file shares: downloads through the link, files sent to a file request */
  downloads: z.number().int().optional(),
  drops: z.number().int().optional(),
})
export type GrantStatus = z.infer<typeof grantStatus>

/** Describe a scope for a person: "tick and add on list …" */
export const scopeTarget = (scope: string): { kind: string; id: string; write: boolean } | null => {
  const m = /^(list|page|task|project):([0-9a-f-]{36}):(read|write)$/.exec(scope)
  if (!m) return null
  const kind = { list: 'List', page: 'Page', task: 'Task', project: 'Project' }[m[1]!]!
  return { kind, id: m[2]!, write: m[3] === 'write' }
}

/** A file share's target: a file or folder to read, or a folder to drop files into (mirrors grant.FileShareOf). */
export const fileShareOf = (spec: { scopes: string[] }): { id: string; drop: boolean } | null => {
  for (const sc of spec.scopes) {
    const m = /^file:([0-9a-f-]{36}):(read|drop)$/.exec(sc)
    if (m) return { id: m[1]!, drop: m[2] === 'drop' }
  }
  return null
}

/** The workspace a scope signs in to ("workspace:home:write" → home), when it is one. */
export const memberScope = (scope: string): { slug: string; write: boolean } | null => {
  const m = /^workspace:([a-z0-9][a-z0-9-]{0,30}):(read|write)$/.exec(scope)
  return m ? { slug: m[1]!, write: m[2] === 'write' } : null
}

/**
 * What a member is called: the name the owner set, else the grant's name without its
 * possessive tail ("Keira’s Shortcut" → "Keira"), else her handle (member.DisplayName in Go).
 */
export const memberName = (spec: { name?: string; member_name?: string; from?: string }): string => {
  const set = spec.member_name?.trim()
  if (set) return set
  const name = spec.name?.trim() ?? ''
  const first = [name.indexOf('’s '), name.indexOf("'s ")].filter((i) => i > 0)
  if (first.length) return name.slice(0, Math.min(...first)).trim()
  if (name) return name
  const from = spec.from?.trim()
  return from ? from.charAt(0).toUpperCase() + from.slice(1) : 'A member'
}
